import { useState, FormEvent } from 'react'
import { Link } from 'react-router-dom'
import emailjs from '@emailjs/browser'

// EmailJS Configuration
const EMAILJS_SERVICE_ID = 'service_fhwace2'
const EMAILJS_PUBLIC_KEY = 'IB5j2dmRSF9DxPdIe'
// Using same template - will format for newsletter signup
const EMAILJS_TEMPLATE_ID = 'template_z3sq9uc'

const Footer = () => {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const handleNewsletterSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!email) return

    setIsSubmitting(true)
    
    try {
      // Submit to Netlify Forms
      const formBody = new URLSearchParams()
      formBody.append('form-name', 'newsletter')
      formBody.append('email', email)
      formBody.append('source', 'footer')
      formBody.append('timestamp', new Date().toISOString())

      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      })

      // Send EmailJS notification
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          businessName: '📧 NEWSLETTER SIGNUP',
          category: 'Newsletter Subscription',
          city: 'Footer Form',
          state: 'Website',
          zip: '',
          address: '',
          phone: '',
          email: email,
          website: '',
          instagram: '',
          facebook: '',
          additionalInfo: `New newsletter subscriber from the footer signup form.\n\nEmail: ${email}\nSource: Footer Newsletter Form\nTime: ${new Date().toLocaleString()}`,
          newsletter: 'Yes - Newsletter Signup',
        },
        EMAILJS_PUBLIC_KEY
      )

      setSubmitStatus('success')
      setEmail('')
    } catch {
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const quickLinks = [
    { path: '/', label: 'Home' },
    { path: '/story', label: 'Soul Food Story' },
    { path: '/directory', label: 'Business Directory' },
    { path: '/recipes', label: 'Heritage Recipes' },
    { path: '/history', label: 'Interactive History' },
    { path: '/submit', label: 'Submit Your Business' },
  ]

  return (
    <footer className="bg-rich-mahogany text-warm-cream" role="contentinfo">
      {/* Kente Pattern Border */}
      <div className="h-3 w-full bg-gradient-to-r from-terracotta via-kente-gold to-forest-green"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {/* About Section */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-terracotta to-burnt-sienna rounded-full flex items-center justify-center">
                <span className="text-warm-cream font-crimson font-bold text-lg">BP</span>
              </div>
              <h3 className="font-crimson font-bold text-xl">THE BLACK PLATE</h3>
            </div>
            <p className="text-warm-cream/80 text-sm leading-relaxed mb-4">
              Soul food is not simply a cuisine. It is a repository of memory, a chronicle of survival, 
              and a declaration of self-determination. Every meal at a Black-owned restaurant is an 
              act of resistance. Every dollar spent is a seed planted for the future.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-warm-cream/10 rounded-full flex items-center justify-center hover:bg-terracotta transition-colors"
                aria-label="Follow us on Twitter"
              >
                <i className="fab fa-twitter" aria-hidden="true"></i>
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-warm-cream/10 rounded-full flex items-center justify-center hover:bg-terracotta transition-colors"
                aria-label="Follow us on Facebook"
              >
                <i className="fab fa-facebook-f" aria-hidden="true"></i>
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-warm-cream/10 rounded-full flex items-center justify-center hover:bg-terracotta transition-colors"
                aria-label="Follow us on Instagram"
              >
                <i className="fab fa-instagram" aria-hidden="true"></i>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-crimson font-bold text-xl mb-4 text-golden-amber">
              Quick Links
            </h3>
            <nav aria-label="Footer navigation">
              <ul className="space-y-2">
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-warm-cream/80 hover:text-golden-amber transition-colors text-sm flex items-center"
                    >
                      <i className="fas fa-chevron-right text-xs mr-2 text-terracotta" aria-hidden="true"></i>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Newsletter & Contact */}
          <div>
            <h3 className="font-crimson font-bold text-xl mb-4 text-golden-amber">
              Stay Connected
            </h3>
            <p className="text-warm-cream/80 text-sm mb-4">
              Join The Black Plate community. Get updates on new businesses and soul food heritage content.
            </p>
            
            {/* Newsletter Form - Netlify Forms */}
            <form
              name="newsletter"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
              onSubmit={handleNewsletterSubmit}
              className="mb-6"
            >
              <input type="hidden" name="form-name" value="newsletter" />
              <p className="hidden">
                <label>
                  Don't fill this out: <input name="bot-field" />
                </label>
              </p>
              
              <div className="flex gap-2">
                <label htmlFor="footer-email" className="sr-only">
                  Email address
                </label>
                <input
                  type="email"
                  id="footer-email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="flex-1 px-4 py-2 rounded-lg bg-warm-cream/10 border border-warm-cream/20 text-warm-cream placeholder-warm-cream/50 focus:outline-none focus:border-golden-amber transition-colors"
                  disabled={isSubmitting}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-terracotta hover:bg-burnt-sienna rounded-lg font-semibold transition-colors disabled:opacity-50"
                  aria-label="Subscribe to newsletter"
                >
                  {isSubmitting ? (
                    <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                  ) : (
                    <i className="fas fa-paper-plane" aria-hidden="true"></i>
                  )}
                </button>
              </div>
              
              {submitStatus === 'success' && (
                <p className="mt-2 text-forest-green text-sm" role="alert">
                  <i className="fas fa-check-circle mr-1" aria-hidden="true"></i>
                  Thank you for subscribing!
                </p>
              )}
              {submitStatus === 'error' && (
                <p className="mt-2 text-red-400 text-sm" role="alert">
                  <i className="fas fa-exclamation-circle mr-1" aria-hidden="true"></i>
                  Something went wrong. Please try again.
                </p>
              )}
            </form>

            {/* Contact Info - small print */}
            <div className="space-y-2" style={{ fontSize: '11px' }}>
              <p className="text-warm-cream/60">
                <i className="fas fa-envelope mr-2 text-terracotta/70" aria-hidden="true"></i>
                <a href="mailto:nommowriters@gmail.com" className="hover:text-golden-amber transition-colors">
                  nommowriters@gmail.com
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-warm-cream/10 mt-10 pt-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-warm-cream/60 text-sm text-center sm:text-left">
              © {new Date().getFullYear()} <a 
                href="https://chicagocrusader.com/tag/stephanie-gadlin/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-golden-amber hover:text-warm-cream transition-colors"
              >Stephanie Gadlin</a>. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <p className="text-warm-cream/60 text-sm">
                <span className="italic">"The seeds remember what the law tried to erase."</span>
              </p>
              {/* Small discrete admin link */}
              <Link 
                to="/admin" 
                className="text-warm-cream/30 hover:text-warm-cream/60 text-xs transition-colors"
                aria-label="Admin access"
              >
                <i className="fas fa-lock" aria-hidden="true"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
